// File: typescript/apps/front-end/src/main/react/page/series-page/SeriesDiscussionListLazy.tsx
//
// Lazy boundary for SeriesDiscussionList (uses @dnd-kit for reordering). SeriesPage is statically
// mounted by ReactMounter, so importing the list directly would ship @dnd-kit on every page. The
// pure ordering helpers were split into ./seriesDiscussionOrdering so SeriesPage imports those without
// the list; the list itself (and @dnd-kit) loads on demand through this wrapper.
// See designs/01-now/2026-06-08-frontend-bundle-code-splitting-implementation-plan.md (slice 4).

import React, { Suspense } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { LazyControlBoundary } from "@/main/react/LazyControlBoundary";
import { CRAWLABLE_DISCUSSION_LIST_SSR_SELECTOR, SsrRestoredList } from "@/main/react/list/ssrFallbacks";

// Type-only import() query: erased at runtime (no @dnd-kit pull), gives the exact prop types.
type SeriesDiscussionListProps = React.ComponentProps<
    (typeof import("@/main/react/page/series-page/SeriesDiscussionList"))["SeriesDiscussionList"]
>;

const SeriesDiscussionListImpl = lazyWithRetry(
    () =>
        import("@/main/react/page/series-page/SeriesDiscussionList").then((m) => ({
            default: m.SeriesDiscussionList,
        })),
    "series-discussion-list",
);

function SeriesDiscussionListFallback() {
    return (
        <div className="w-full animate-pulse space-y-3" aria-hidden="true">
            <div className="h-10 w-48 rounded-xl bg-muted" />
            <div className="h-28 w-full rounded-2xl border border-border/60 bg-card" />
            <div className="h-28 w-full rounded-2xl border border-border/60 bg-card" />
            <div className="h-28 w-full rounded-2xl border border-border/60 bg-card" />
        </div>
    );
}

export function SeriesDiscussionList(props: SeriesDiscussionListProps) {
    // Containment (FI-070, 2026-09-15): a chunk that never arrives costs the list, not the SeriesPage
    // island, and what the member already had is the server's own crawlable list (series.jte), so that
    // goes back: the same `SsrRestoredList` the list's own terminal-fetch branch uses, marked the same
    // way for `islandHealthGuard.mjs`. Null on a client-owned arrival with no snapshot.
    return (
        <LazyControlBoundary
            controlName="series-discussion-list"
            fallback={<SsrRestoredList componentName="SeriesPage" selector={CRAWLABLE_DISCUSSION_LIST_SSR_SELECTOR} />}
        >
            <Suspense fallback={<SeriesDiscussionListFallback />}>
                <SeriesDiscussionListImpl {...props} />
            </Suspense>
        </LazyControlBoundary>
    );
}
